import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import Checkbox from '../../common/Checkbox';
import {
    setTicketSideDrawer,
} from '../../actions/tickets';
import { useDispatch, useSelector } from 'react-redux';
import OverflowTip from '../../common/OverflowTip';
import { isCluster, isSuperVisor } from '../../utils/role';
import {
    allTab,
    newAndCustomerTab,
    triggerTab,
    ongoingTab,
    IssuesTab,
    completedTab,
    scheduledTab,
    columnsKeys,
    defaultRowsPerPage,
    defaultPage,
    ticketExtraColumns,
    todayStatus,
    canEditTicket,
    TicketAgentRole,
    RegionsType,
    TicketMilestoneEvents,
} from './TicketController';
import { LineClampComponent } from './TicketTableComponent';
import { getDateWithISOFormat, readFilterQueryParams, transformQueryFilters, updateQueryParamsForTickets, validateDate, validateFilterString } from '../../utils/filter';
import APIFilterPopup from '../../common/APIFilters/FilterPopup';
import { useState } from 'react';
import { debounce, GetQueryParam, useQuery, validateStringValue } from '../../utils/common';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AssetsColumn } from './TicketColumns/Asset';
import { RemarkColumn } from './TicketColumns/Remark';
import { PartnerDetail } from './TicketColumns/Partner';
import { ProgressBar } from './TicketColumns/Progress';
import { StatusColumn } from './TicketColumns/Status';
import { TicketAction } from './TicketColumns/Actions';
import { Price } from './TicketColumns/Price';
import FrozenTable from '../../common/FrozenTable';
import { tickets } from '../../types';
import { Shipping } from './TicketColumns/Shipping';
import SiteColumn from './TicketColumns/Sites';
import TicketNumber from './TicketColumns/TicketNumber';
import Contact from './TicketColumns/Contact';
import { getServiceRequestCountByStatus, getServiceRequestFilterList, getServiceRequestList } from '../../actions/service_request';
import { getPreferences } from '../../actions/utils';
import { canManageOtherOrgs } from '../../utils/access';
import TodayStatusTable from './TodayStatusTable';
import { OwnerColumn } from './TicketColumns/Owner';
import { isMobileView } from '../../utils/utils';
import { DateComponent } from '../../common/TableReusableColumns';
import { useRef } from 'react';

export const ticketFilterSchema = {
    city: {
        filterName: 'city',
        title: 'City',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ city } = {}) => {
            const values = validateFilterString(city);
            return values ? { [RegionsType.CITY]: values } : {};
        },
        queryParamKey: 'city',
        parse: ({ city }) => {
            const values = validateFilterString(city);
            return values ? { city: values } : {};
        },
        filter_label: 'city'
    },
    state: {
        filterName: 'state',
        title: 'States',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ state } = {}) => {
            const values = validateFilterString(state);
            return values ? { [RegionsType.STATE]: values } : {};
        },
        queryParamKey: 'state',
        parse: ({ state }) => {
            const values = validateFilterString(state);
            return values ? { state: values } : {};
        },
        filter_label: 'state'
    },
    localities: {
        filterName: 'locality',
        title: 'Localities',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ locality } = {}) => {
            const values = validateFilterString(locality);
            return values ? { [RegionsType.LOCALITY]: values } : {};
        },
        queryParamKey: 'locality',
        parse: ({ locality }) => {
            const values = validateFilterString(locality);
            return values ? { locality: values } : {};
        },
        filter_label: 'locality'
        // sieveForFilterItems: ({ currentFilter, filterList }) => {
        //     let filteredItems;
        //     if (currentFilter.states) {
        //         const states = currentFilter.states.filter((state) => state.isSelected).map((state) => state.apiValue);
        //         const localities = filterList.localities.filter((loc) => states.includes(loc.fk_state_id));
        //         filteredItems = localities;
        //     }
        //     if (currentFilter.cities) {
        //         const cities = currentFilter.cities.filter((state) => state.isSelected).map((state) => state.apiValue);
        //         const localities = filterList.localities.filter((loc) => cities.includes(loc.fk_city_id));
        //         filteredItems = localities;
        //     }
        //     return filteredItems;
        // },
    },
    districts: {
        filterName: 'district',
        title: 'Districts',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ district } = {}) => {
            const values = validateFilterString(district);
            return values ? { [RegionsType.DISTRICT]: values } : {};
        },
        queryParamKey: 'district',
        parse: ({ district }) => {
            const values = validateFilterString(district);
            return values ? { district: values } : {};
        },
        // sieveForFilterItems: ({ currentFilter, filterList }) => {
        //     let filteredItems;
        //     if (currentFilter.states) {
        //         const states = currentFilter.states.filter((state) => state.isSelected).map((state) => state.apiValue);
        //         const districts = filterList.districts.filter((dist) => states.includes(dist.fk_state_id));
        //         filteredItems = districts;
        //     }
        //     return filteredItems;
        // },
        filter_label: 'district'
    },
    region_tier: {
        filterName: 'region_tier',
        title: 'Tier',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ region_tier } = {}) => {
            const values = validateFilterString(region_tier);
            return values ? { [RegionsType.REGION_TIER]: values } : {};
        },
        queryParamKey: 'region_tier',
        parse: ({ region_tier }) => {
            const values = validateFilterString(region_tier);
            return values ? { region_tier: values } : {};
        },
    },
    projects: {
        filterName: 'projects',
        title: 'Projects',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ project_id } = {}) => {
            const values = validateFilterString(project_id);
            return values ? { project_id: values.join(',') } : {};
        },
        queryParamKey: 'project_id',
        parse: ({ project_id }) => {
            const values = validateFilterString(project_id);
            return values ? { project_id: values } : {};
        },
        filter_label: 'projects'
    },
    milestoneStatus: {
        filterName: 'milestoneStatus',
        title: 'Status',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ milestone } = {}) => {
            let values = validateFilterString(milestone);
            if (!values) return {};
            const obj = {};
            const filteredValues = values.filter(val => val !== 'SIGN_OFF_LOCKED' && val !== 'SIGN_OFF_APPROVAL_LOCKED' && val !== 'ON_HOLD' && val !== 'RMA_PENDING' && val !== 'RMA_UPDATED_DEVICE_CONSUMED_FULL' && val !== 'RMA_UPDATED_PART_CONSUMED' && val !== 'RMA_UPDATED_NO_DEVICE_PARTS');

            if (filteredValues.length > 0) {
                obj.milestone_status = filteredValues.join(',');
            }

            if (values.find(val => val === 'SIGN_OFF_LOCKED')) {
                obj.sign_off_lock = true;
            }
            if (values.find(val => val === 'SIGN_OFF_APPROVAL_LOCKED')) {
                obj.sign_off_approve_lock = true;
            }

            if (values.find(val => val === 'ON_HOLD')) {
                obj.is_hold = true;
            }

            if (values.find(val => val === 'RMA_PENDING')) {
                obj.is_rma_pending = true;
            }
            const rmaConsumptionTypes = [];
            if (values.find(val => val === 'RMA_UPDATED_DEVICE_CONSUMED_FULL')) {
                rmaConsumptionTypes.push('full');
            }
            if (values.find(val => val === 'RMA_UPDATED_PART_CONSUMED')) {
                rmaConsumptionTypes.push('partial');
            }
            if (values.find(val => val === 'RMA_UPDATED_NO_DEVICE_PARTS')) {
                rmaConsumptionTypes.push('none');
            }
            if (rmaConsumptionTypes.length > 0) {
                obj.rma_consumption_type = rmaConsumptionTypes.join(',');
            }

            return obj;
        },
        queryParamKey: 'milestone',
        parse: ({ milestone }) => {
            const values = validateFilterString(milestone);
            return values ? { milestone: values } : {};
        },
    },
    ticketStatus: {
        filterName: 'ticketStatus',
        title: 'Status',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ status } = {}) => {
            const values = validateFilterString(status);
            if (values && values?.includes?.('ON_HOLD')) {
                values.push('REJECTED');
            }
            if (values && values.includes('NEW')) {
                const index = values.indexOf('NEW');
                values[index] = 'TRIGGERED';
            }
            return values ? { status: values.join(',') } : {};
        },
        queryParamKey: 'status',
        parse: ({ status }) => {
            const values = validateFilterString(status);
            return values ? { status: values } : {};
        },
    },
    ticketStatusSpFe: {
        filterName: 'ticketStatusSpFe',
        title: 'Status',
        typeOfFilter: 'checkbox',
        apiValueKey: 'key',
        displayValueKey: 'label',
        submitKey: 'labels',
        queryParamKey: 'labels',
    },
    customer: {
        filterName: 'org',
        title: 'Customer',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ org_id } = {}) => {
            const values = validateFilterString(org_id);
            return values ? { org_id: values.join(',') } : {};
        },
        queryParamKey: 'org_id',
        parse: ({ org_id }) => {
            const values = validateFilterString(org_id);
            return values ? { org_id: values } : {};
        },
        filter_label: 'org'
    },
    endUser: {
        filterName: 'enduser',
        title: 'Enduser',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ end_user_id } = {}) => {
            const values = validateFilterString(end_user_id);
            return values ? { end_user_id: values.join(',') } : {};
        },
        queryParamKey: 'end_user_id',
        parse: ({ end_user_id }) => {
            const values = validateFilterString(end_user_id);
            return values ? { end_user_id: values } : {};
        },
        filter_label: 'enduser'
    },
    partners: {
        filterName: 'SP',
        title: 'SP',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ SP } = {}) => {
            const values = validateFilterString(SP);
            return values ? { [TicketAgentRole.SP]: values } : {};
        },
        queryParamKey: 'SP',
        parse: ({ SP }) => {
            const values = validateFilterString(SP);
            return values ? { SP: values } : {};
        },
        filter_label: 'SP'
    },
    engineers: {
        filterName: 'FE',
        title: 'FE',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ FE } = {}) => {
            const values = validateFilterString(FE);
            return values ? { [TicketAgentRole.FE]: values } : {};
        },
        queryParamKey: 'FE',
        parse: ({ FE }) => {
            const values = validateFilterString(FE);
            return values ? { FE: values } : {};
        },
        filter_label: 'FE'
    },
    clusters: {
        filterName: 'CLUSTER',
        title: 'Clusters',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ CLUSTER } = {}) => {
            const values = validateFilterString(CLUSTER);
            return values ? { [TicketAgentRole.CLUSTER]: values } : {};
        },
        queryParamKey: 'CLUSTER',
        parse: ({ CLUSTER }) => {
            const values = validateFilterString(CLUSTER);
            return values ? { CLUSTER: values } : {};
        },
        filter_label: 'CLUSTER'
    },
    psp: {
        filterName: 'PSP',
        title: 'PSP',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ PS_P } = {}) => {
            const values = validateFilterString(PS_P);
            return values ? { [TicketAgentRole.PSP]: values } : {};
        },
        queryParamKey: 'PS_P',
        parse: ({ PS_P }) => {
            const values = validateFilterString(PS_P);
            return values ? { PS_P: values } : {};
        },
        filter_label: 'PS_P'
    },
    supervisor: {
        filterName: 'SUPERVISOR',
        title: 'Supervisor',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ SUPERVISOR } = {}) => {
            const values = validateFilterString(SUPERVISOR);
            return values ? { [TicketAgentRole.SUPERVISOR]: values } : {};
        },
        queryParamKey: 'SUPERVISOR',
        parse: ({ SUPERVISOR }) => {
            const values = validateFilterString(SUPERVISOR);
            return values ? { SUPERVISOR: values } : {};
        },
        filter_label: 'SUPERVISOR'
    },
    ticketType: {
        filterName: 'milestoneType',
        title: 'Milestone Type',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ milestone_type } = {}) => {
            const values = validateFilterString(milestone_type);
            return values ? { milestone_type: values.join(',') } : {};
        },
        queryParamKey: 'milestone_type',
        parse: ({ milestone_type }) => {
            const values = validateFilterString(milestone_type);
            return values ? { milestone_type: values } : {};
        },
    },
    requestedBy: {
        filterName: 'requester',
        title: 'Ticket Creator',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ requester } = {}) => {
            const values = validateFilterString(requester);
            return values ? { requested_by: values.join(',') } : {};
        },
        queryParamKey: 'requester',
        parse: ({ requester }) => {
            const values = validateFilterString(requester);
            return values ? { requester: values } : {};
        },
        filter_label: 'requester'
    },
    financialState: {
        filterName: 'financialState',
        title: 'Financial State',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ financialState } = {}) => {
            const values = validateFilterString(financialState);
            return values ? { financial_state: values.join(',') } : {};
        },
        queryParamKey: 'financialState',
        parse: ({ financialState }) => {
            const values = validateFilterString(financialState);
            return values ? { financialState: values } : {};
        },
        filter_label: 'financialState'
    },
    executionDate: {
        filterName: 'executionDate',
        title: 'Execution Date',
        typeOfFilter: 'daterange',
        submitKey: ({ eFromDate, eToDate } = {}) => {
            if (!eFromDate || !eToDate) return {};
            const startOfDay = new Date(eFromDate.getFullYear(), eFromDate.getMonth(), eFromDate.getDate(), 0, 0, 0).toISOString();
            const endOfDay = new Date(eToDate.getFullYear(), eToDate.getMonth(), eToDate.getDate(), 23, 59, 59).toISOString();
            return { execution_dates_range: [startOfDay, endOfDay].join(',') }
        },
        queryParamKey: {
            keyList: ['eFromDate', 'eToDate'],
            getQueryObject: ({ fromDate, toDate }) => ({ eFromDate: fromDate, eToDate: toDate }),
        },
        parse: ({ eFromDate, eToDate }) => {
            let validatedFrom = validateDate(eFromDate, { cutoffDate: false });
            let validatedTo = validateDate(eToDate, { cutoffDate: false });
            if (validatedFrom > validatedTo) return {};
            if (validatedFrom && validatedTo) {
                return { eFromDate: validatedFrom, eToDate: validatedTo };
            } else if (validatedFrom) {
                return { eFromDate: validatedFrom, eToDate: new Date() };
            }
            return {};
        },
        getSelectedFilterValues: ({ eFromDate, eToDate } = {}) => ({
            fromDate: getDateWithISOFormat(eFromDate),
            toDate: getDateWithISOFormat(eToDate),
        }),
    },
    orderDate: {
        filterName: 'orderDate',
        title: 'Created At',
        typeOfFilter: 'daterange',
        submitKey: ({ oFromDate, oToDate } = {}) => ({
            creation_dates_range: [getDateWithISOFormat(oFromDate), getDateWithISOFormat(oToDate)].join(','),
        }),
        queryParamKey: {
            keyList: ['oFromDate', 'oToDate'],
            getQueryObject: ({ fromDate, toDate }) => ({ oFromDate: fromDate, oToDate: toDate }),
        },
        parse: ({ oFromDate, oToDate }) => {
            let validatedFrom = validateDate(oFromDate, { cutoffDate: false });
            let validatedTo = validateDate(oToDate, { cutoffDate: false });
            if (validatedFrom > validatedTo) return {};
            if (validatedFrom && validatedTo) {
                return { oFromDate: validatedFrom, oToDate: validatedTo };
            } else if (validatedFrom) {
                return { oFromDate: validatedFrom, oToDate: new Date() };
            }
            return {};
        },
        getSelectedFilterValues: ({ oFromDate, oToDate } = {}) => ({
            fromDate: getDateWithISOFormat(oFromDate),
            toDate: getDateWithISOFormat(oToDate),
        }),
    },
    siteComplete: {
        filterName: 'siteComplete',
        title: 'Site Complete',
        typeOfFilter: 'daterange',
        submitKey: ({ scFromDate, scToDate } = {}) => ({
            closed_dates_range: [getDateWithISOFormat(scFromDate), getDateWithISOFormat(scToDate)].join(','),
        }),
        queryParamKey: {
            keyList: ['scFromDate', 'scToDate'],
            getQueryObject: ({ fromDate, toDate }) => ({ scFromDate: fromDate, scToDate: toDate }),
        },
        parse: ({ scFromDate, scToDate }) => {
            let validatedFrom = validateDate(scFromDate, { cutoffDate: false });
            let validatedTo = validateDate(scToDate, { cutoffDate: false });
            if (validatedFrom > validatedTo) return {};
            if (validatedFrom && validatedTo) {
                return { scFromDate: validatedFrom, scToDate: validatedTo };
            } else if (validatedFrom) {
                return { scFromDate: validatedFrom, scToDate: new Date() };
            }
            return {};
        },
        getSelectedFilterValues: ({ scFromDate, scToDate } = {}) => ({
            fromDate: getDateWithISOFormat(scFromDate),
            toDate: getDateWithISOFormat(scToDate),
        }),
    },
    signOffApprovalDate: {
        filterName: 'signOffApprovalDate',
        title: 'Signoff Approval Date',
        typeOfFilter: 'daterange',
        submitKey: ({ saFromDate, saToDate } = {}) => ({
            approval_dates_range: [getDateWithISOFormat(saFromDate), getDateWithISOFormat(saToDate)].join(','),
        }),
        queryParamKey: {
            keyList: ['saFromDate', 'saToDate'],
            getQueryObject: ({ fromDate, toDate }) => ({ saFromDate: fromDate, saToDate: toDate }),
        },
        parse: ({ saFromDate, saToDate }) => {
            let validatedFrom = validateDate(saFromDate, { cutoffDate: false });
            let validatedTo = validateDate(saToDate, { cutoffDate: false });
            if (validatedFrom > validatedTo) return {};
            if (validatedFrom && validatedTo) {
                return { saFromDate: validatedFrom, saToDate: validatedTo };
            } else if (validatedFrom) {
                return { saFromDate: validatedFrom, saToDate: new Date() };
            }
            return {};
        },
        getSelectedFilterValues: ({ saFromDate, saToDate } = {}) => ({
            fromDate: getDateWithISOFormat(saFromDate),
            toDate: getDateWithISOFormat(saToDate),
        }),
    },
    assignmentType: {
        filterName: 'assignmentTypes',
        title: 'Assignment Type',
        typeOfFilter: 'checkbox',
        apiValueKey: 'id',
        displayValueKey: 'key',
        submitKey: 'assignmentTypes',
        queryParamKey: 'assignmentTypes',
    },
    issueCategories: {
        filterName: 'issueCategories',
        title: 'Issue Category',
        typeOfFilter: 'checkbox',
        apiValueKey: 'id',
        displayValueKey: 'label',
        submitKey: ({ issueCategoryIds } = {}) => {
            const possibleEvents = [72, 73, 31, 83];
            let eventIds = issueCategoryIds?.filter((value) => possibleEvents.includes(value));
            let categoryIds = issueCategoryIds?.filter((value) => !possibleEvents.includes(value));
            let data = {};
            if (categoryIds?.length > 0) data.issueCategoryIds = categoryIds;
            if (eventIds?.length > 0) data.eventIds = eventIds;
            return data;
        },
        queryParamKey: 'issueCategoryIds',
        parse: ({ issueCategoryIds }) => {
            const values = validateFilterString(issueCategoryIds);
            return values ? { issueCategoryIds: values } : {};
        },
    },
    agent: {
        filterName: 'REMOTE_SUPPORT',
        title: 'Remote Support',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ REMOTE_SUPPORT } = {}) => {
            const values = validateFilterString(REMOTE_SUPPORT);
            return values ? { [TicketAgentRole.REMOTE_SUPPORT]: values } : {};
        },
        queryParamKey: 'REMOTE_SUPPORT',
        parse: ({ REMOTE_SUPPORT }) => {
            const values = validateFilterString(REMOTE_SUPPORT);
            return values ? { REMOTE_SUPPORT: values } : {};
        },
        filter_label: 'REMOTE_SUPPORT'
    },
    ticket_serial_number: {
        filterName: 'ticket_serial_number',
        title: 'Ticket Serial Number',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ ticket_serial_number } = {}) => {
            return ticket_serial_number ? { serial_number: decodeURIComponent(ticket_serial_number.replace(/\+/g, ' ')) } : {};
        },
        queryParamKey: 'ticket_serial_number',
        parse: ({ ticket_serial_number }) => {
            return ticket_serial_number ? { ticket_serial_number: ticket_serial_number?.[0] } : {};
        },
        type: 'search_filter'
    },
    ticket_number: {
        filterName: 'ticket_number',
        title: 'Ticket Number',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ ticket_number } = {}) => {
            return ticket_number ? { ticket_number: decodeURIComponent(ticket_number.replace(/\+/g, ' ')) } : {};
        },
        queryParamKey: 'ticket_number',
        parse: ({ ticket_number }) => {
            return ticket_number ? { ticket_number: ticket_number?.[0] } : {};
        },
        type: 'search_filter'
    },
    ticket_location_external_id: {
        filterName: 'ticket_location_external_id',
        title: 'Ticket Location External ID',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ ticket_location_external_id } = {}) => {
            return ticket_location_external_id ? { external_ids: decodeURIComponent(ticket_location_external_id.replace(/\+/g, ' ')) } : {};
        },
        queryParamKey: 'ticket_location_external_id',
        parse: ({ ticket_location_external_id }) => {
            return ticket_location_external_id ? { ticket_location_external_id: ticket_location_external_id?.[0] } : {};
        },
        type: 'search_filter'
    },
    ticket_pincode: {
        filterName: 'ticket_pincode',
        title: 'Ticket Pincode',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ ticket_pincode } = {}) => {
            return ticket_pincode ? { pincode: decodeURIComponent(ticket_pincode.replace(/\+/g, ' ')) } : {};
        },
        queryParamKey: 'ticket_pincode',
        parse: ({ ticket_pincode }) => {
            return ticket_pincode ? { ticket_pincode: ticket_pincode?.[0] } : {};
        },
        type: 'search_filter'
    },
    ticket_external_ids: {
        filterName: 'ticket_external_ids',
        title: 'Ticket External IDs',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ ticket_external_ids } = {}) => {
            return ticket_external_ids ? { external_ticket_ids: decodeURIComponent(ticket_external_ids.replace(/\+/g, ' ')) } : {};
        },
        queryParamKey: 'ticket_external_ids',
        parse: ({ ticket_external_ids }) => {
            return ticket_external_ids ? { ticket_external_ids: ticket_external_ids?.[0] } : {};
        },
        type: 'search_filter'
    },
    ticketInvoiceGenerated: {
        filterName: 'ticketInvoiceGenerated',
        title: 'Invoice Generated',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ ticketInvoiceGenerated } = {}) => {
            const values = validateFilterString(ticketInvoiceGenerated);
            let d = {};
            if (values && values.includes('yes')) {
                d.invoice_generated = true;
            }
            if (values && values.includes('no')) {
                d.invoice_generated = false;
            }
            if (values && values.includes('customer_billing_approved')) {
                d.customer_approval_received = true;
            }
            return d;
        },
        queryParamKey: 'ticketInvoiceGenerated',
        parse: ({ ticketInvoiceGenerated }) => {
            const values = validateFilterString(ticketInvoiceGenerated);
            return values ? { ticketInvoiceGenerated: values } : {};
        },
    },
    q_ticket_number: {
        filterName: 'q_t_n',
        title: 'Ticket Number Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_t_n } = {}) => {
            return q_t_n ? { q_ticket_number: decodeURIComponent(q_t_n.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_t_n',
        parse: ({ q_t_n }) => {
            return q_t_n ? { q_t_n: q_t_n?.[0] } : {};
        },
        type: 'search_filter'
    },
    q_external_ticket_id: {
        filterName: 'q_e_t_i',
        title: 'Ticket Number Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_e_t_i } = {}) => {
            return q_e_t_i ? { q_external_ticket_id: decodeURIComponent(q_e_t_i.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_e_t_i',
        parse: ({ q_e_t_i }) => {
            return q_e_t_i ? { q_e_t_i: q_e_t_i?.[0] } : {};
        },
        type: 'search_filter'
    },
    q_external_id: {
        filterName: 'q_e_i',
        title: 'External Id Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_e_i } = {}) => {
            return q_e_i ? { q_external_id: decodeURIComponent(q_e_i.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_e_i',
        parse: ({ q_e_i }) => {
            return q_e_i ? { q_e_i: q_e_i?.[0] } : {};
        },
        type: 'search_filter'
    },
    q_serial_number: {
        filterName: 'q_s_n',
        title: 'Serial Number Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_s_n } = {}) => {
            return q_s_n ? { q_serial_number: decodeURIComponent(q_s_n.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_s_n',
        parse: ({ q_s_n }) => {
            return q_s_n ? { q_s_n: q_s_n?.[0] } : {};
        },
        type: 'search_filter'
    },
    q_local_contact_name: {
        filterName: 'q_l_c_n',
        title: 'Local Contact Name Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_l_c_n } = {}) => {
            return q_l_c_n ? { q_local_contact_name: decodeURIComponent(q_l_c_n.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_l_c_n',
        parse: ({ q_l_c_n }) => {
            return q_l_c_n ? { q_l_c_n: q_l_c_n?.[0] } : {};
        },
        type: 'search_filter'
    },
    q_local_contact_number: {
        filterName: 'q_l_c_num',
        title: 'Local Contact Number Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_l_c_num } = {}) => {
            return q_l_c_num ? { q_local_contact_number: decodeURIComponent(q_l_c_num.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_l_c_num',
        parse: ({ q_l_c_num }) => {
            return q_l_c_num ? { q_l_c_num: q_l_c_num?.[0] } : {};
        },
        type: 'search_filter'
    },
    q_city: {
        filterName: 'q_c',
        title: 'City Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_c } = {}) => {
            return q_c ? { q_city: decodeURIComponent(q_c.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_c',
        parse: ({ q_c }) => {
            return q_c ? { q_c: q_c?.[0] } : {};
        },
        type: 'search_filter'
    },
    q_state: {
        filterName: 'q_s',
        title: 'State Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_s } = {}) => {
            return q_s ? { q_state: decodeURIComponent(q_s.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_s',
        parse: ({ q_s }) => {
            return q_s ? { q_s: q_s?.[0] } : {};
        },
        type: 'search_filter'
    },
    q_pincode: {
        filterName: 'q_p',
        title: 'Pincode Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_p } = {}) => {
            return q_p ? { q_pincode: decodeURIComponent(q_p.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_p',
        parse: ({ q_p }) => {
            return q_p ? { q_p: q_p?.[0] } : {};
        },
        type: 'search_filter'
    },
    q_title: {
        filterName: 'q_title',
        title: 'Title Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_title } = {}) => {
            return q_title ? { q_title: decodeURIComponent(q_title.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_title',
        parse: ({ q_title }) => {
            return q_title ? { q_title: q_title?.[0] } : {};
        },
        type: 'search_filter'
    },
    q_desc: {
        filterName: 'q_desc',
        title: 'Description Search',
        typeOfFilter: 'text',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ q_desc } = {}) => {
            return q_desc ? { q_desc: decodeURIComponent(q_desc.replace(/\+/g, ' ')).trim() } : {};
        },
        queryParamKey: 'q_desc',
        parse: ({ q_desc }) => {
            return q_desc ? { q_desc: q_desc?.[0] } : {};
        },
        type: 'search_filter'
    },
    remarksFilter: {
        filterName: 'remarksFilter',
        title: 'Remarks',
        typeOfFilter: 'checkbox',
        apiValueKey: 'value',
        displayValueKey: 'label',
        submitKey: ({ remarksFilter } = {}) => {
            let values = validateFilterString(remarksFilter);
            if (!values) return {};
            const obj = {};
            let ledger_events = [];
            if (values.includes('INCOMING_MISSED_CALL')) {
                ledger_events.push('ticket.inbound.missed');
            }
            if (values.includes('INCOMING_ANSWERED_CALL')) {
                ledger_events.push('ticket.inbound.answered');
            }
            if (values.includes('OUTGOING_CALL')) {
                ledger_events.push('ticket.outbound');
            }
            if (ledger_events.length > 0) {
                obj.ledger_events = ledger_events.join(',');
            }
            return obj;
        },
        queryParamKey: 'remarksFilter',
        parse: ({ remarksFilter }) => {
            const values = validateFilterString(remarksFilter);
            return values ? { remarksFilter: values } : {};
        },
    },
};

export const feTypeLookup = {
    [scheduledTab.path]: 'fe_scheduled_tickets',
    [allTab.path]: '',
    [ongoingTab.path]: 'fe_ongoing_tickets',
    [IssuesTab.path]: 'fe_onhold_tickets',
    [completedTab.path]: 'fe_completed_tickets',
};

export const spTypeLookup = {
    [newAndCustomerTab.path]: 'new',
    [allTab.path]: 'sp_all_tickets',
    [ongoingTab.path]: 'sp_ongoing_tickets',
    [IssuesTab.path]: 'sp_onhold_tickets',
    [completedTab.path]: 'sp_completed_tickets',
};

export const getTicketTabNameOrSortBy = (role, type, tabName) => {
    let sortBy = 'created_at';
    let tab = tabName;
    if (isCluster(role, type) || isSuperVisor(role, type)) {
        sortBy = 'broadcast_timestamp';
        if (tabName === newAndCustomerTab.path) {
            tab = triggerTab.path;
        }
    }
    return { openedTab: tab, sortBy };
};

export const TERMINAL_COLOR = {
    CONNECTED: 'text-scogo2e',
    DISCONNECTED: 'text-scogoclosed',
    WORK_IN_PROGRESS: 'text-scogoorange',
};

export const textLabelClass = 'text-font11 text-scogogray';
export const textValueClass = 'text-font10 text-black w-full font-normal';

export const getLevelId = (tabName) => {
    switch (tabName) {
        case todayStatus.path:
            return 'TODAY_STATUS';
        default:
            return 'ALL';
    }
};

const TicketsTable = forwardRef((props, ref) => {
    const { selectedColumns, setClearFilterButton, searchQueryParam, limitQueryParam, pageQueryParam, queryParams, openedSubTab, openedMaintab, page, rowsPerPage, onINeedRMASupportClick } =
        props;
    const dispatch = useDispatch();
    const { isTicketFilterListLoading, ticketAssetRemarkLoading } = useSelector((state) => state.loading);
    const { ticketRemoteTerminal } = useSelector((state) => state.tickets);
    const [checkedTicketTracker, setCheckedTicketTracker] = useState(() => new Set());

    const [filterList, setFilterList] = useState({});
    const [filterQueryObject, setFilterObject] = useState({});
    const [filtersActiveStatus, setFiltersActiveStatus] = useState({});
    const query = useQuery();
    const navigate = useNavigate();
    const projectIdsQueryParam = GetQueryParam('project_id');
    const serviceRequestList = useSelector((state) => state.serviceRequest.serviceRequestList);
    const isServiceRequestListLoading = useSelector((state) => state.loading.isServiceRequestListLoading);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const manageOtherOrgs = canManageOtherOrgs(newLoggedUserData.actions);
    const [searchParams, setSearchParams] = useSearchParams();
    const serviceRequestFilterList = useSelector((state) => state.serviceRequest.serviceRequestFilterList);
    const preferenceData = useSelector((state) => state.utils.preferenceData);
    const ticketColumns = useSelector((state) => state.utils.ticketColumns);
    const showTicketSideDrawer = useSelector((state) => state.tickets.showTicketSideDrawer);

    const refCallCountApi = useRef(true);

    const memoizedServiceRequestCount = useMemo(() => {
        let c = 0;
        if (serviceRequestList?.metadata?.total_count) {
            c = serviceRequestList.metadata.total_count;
        }
        return c;
    }, [serviceRequestList?.metadata?.total_count]);

    // useEffect(() => {
    //     dispatch(getServiceRequestCountByStatus());
    // }, [dispatch]);

    useEffect(() => {
        return () => setCheckedTicketTracker(new Set());
    }, [isServiceRequestListLoading]);

    useImperativeHandle(ref, () => ({
        getSelectedTickets: () => {
            let selectedTickets = serviceRequestList.data.filter((_, index) => checkedTicketTracker.has(index));
            return selectedTickets;
        },
    }));

    const handleOnChangeCheckbox = (position) => {
        if (checkedTicketTracker.has(position)) {
            setCheckedTicketTracker((prev) => {
                const next = new Set(prev);
                next.delete(position);
                return next;
            });
        } else {
            setCheckedTicketTracker((prev) => new Set(prev).add(position));
        }
    };

    const selectALLHandler = (checked) => {
        if (checked) {
            const allIndices = serviceRequestList.data.map((_, index) => index);
            setCheckedTicketTracker(new Set(allIndices));
        } else {
            setCheckedTicketTracker(new Set());
        }
    };

    const getTicketsList = useCallback(
        ({ searchTerm, limit, page, payload, openedTab }) => {
            let p = { ...payload };
            if (openedTab === todayStatus.path) {
                const today = new Date();
                const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0).toISOString();
                const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString();
                p.execution_dates_range = `${startOfDay},${endOfDay}`;
            }
            let s = searchTerm;
            if (searchTerm && searchTerm.startsWith('___')) s = '';
            let region_ids = [];
            let region_tier = [];
            let sp_agent_id = [];
            let fe_agent_id = [];
            let cluster_agent_id = [];
            let psp_agent_id = [];
            let supervisor_agent_id = [];
            let remote_support_agent_id = [];

            if (payload[TicketAgentRole.SUPERVISOR] && Array.isArray(payload[TicketAgentRole.SUPERVISOR])) {
                supervisor_agent_id.push(...payload[TicketAgentRole.SUPERVISOR])
            }

            if (payload[TicketAgentRole.PSP] && Array.isArray(payload[TicketAgentRole.PSP])) {
                psp_agent_id.push(...payload[TicketAgentRole.PSP])
            }

            if (payload[TicketAgentRole.CLUSTER] && Array.isArray(payload[TicketAgentRole.CLUSTER])) {
                cluster_agent_id.push(...payload[TicketAgentRole.CLUSTER])
            }

            if (payload[TicketAgentRole.SP] && Array.isArray(payload[TicketAgentRole.SP])) {
                sp_agent_id.push(...payload[TicketAgentRole.SP])
            }

            if (payload[TicketAgentRole.FE] && Array.isArray(payload[TicketAgentRole.FE])) {
                fe_agent_id.push(...payload[TicketAgentRole.FE])
            }

            if (payload[TicketAgentRole.REMOTE_SUPPORT] && Array.isArray(payload[TicketAgentRole.REMOTE_SUPPORT])) {
                remote_support_agent_id.push(...payload[TicketAgentRole.REMOTE_SUPPORT])
            }


            if (payload[RegionsType.STATE] && Array.isArray(payload[RegionsType.STATE])) {
                region_ids.push(...payload[RegionsType.STATE])
            }
            if (payload[RegionsType.CITY] && Array.isArray(payload[RegionsType.CITY])) {
                region_ids.push(...payload[RegionsType.CITY])
            }
            if (payload[RegionsType.DISTRICT] && Array.isArray(payload[RegionsType.DISTRICT])) {
                region_ids.push(...payload[RegionsType.DISTRICT])
            }
            if (payload[RegionsType.LOCALITY] && Array.isArray(payload[RegionsType.LOCALITY])) {
                region_ids.push(...payload[RegionsType.LOCALITY])
            }
            if (payload[RegionsType.REGION_TIER] && Array.isArray(payload[RegionsType.REGION_TIER])) {
                region_tier.push(...payload[RegionsType.REGION_TIER])
            }

            if (sp_agent_id.length > 0) p.sp_agent_id = sp_agent_id.join(',');
            if (fe_agent_id.length > 0) p.fe_agent_id = fe_agent_id.join(',');
            if (cluster_agent_id.length > 0) p.cluster_agent_id = cluster_agent_id.join(',');
            if (psp_agent_id.length > 0) p.psp_agent_id = psp_agent_id.join(',');
            if (supervisor_agent_id.length > 0) p.supervisor_agent_id = supervisor_agent_id.join(',');
            if (remote_support_agent_id.length > 0) p.remote_support_agent_id = remote_support_agent_id.join(',');

            if (payload.is_hold) {
                p.status = p.status ? `${p.status},ON_HOLD` : 'ON_HOLD'
                delete p.is_hold;
            };

            if (region_ids.length > 0) p.region_ids = region_ids.join(',');
            if (region_tier.length > 0) p.region_tier = region_tier.join(',')
            let callCountApi = (refCallCountApi.current || (Object.keys(p).length > 0 || (searchTerm && !searchTerm.startsWith('___')))) ? true : false;
            if (page === 1) callCountApi = true;
            dispatch(getServiceRequestList({ limit, page, query: s, payload: p, tab: openedTab, callCountApi: callCountApi }));
        },
        [dispatch]
    );

    const getFilterList = useCallback(({ filter_labels = 'projects,district,locality,state,city,pin,org,enduser,requester,WAREHOUSE_OWNER,CLUSTER,SP,SCM,FE,PM,PSP,SUPERVISOR,REQUESTER,REMOTE_SUPPORT', project_ids, q = '', formName }) => {
        dispatch(getServiceRequestFilterList({ filter_labels, project_ids, q, formName }));
    }, [dispatch])

    useEffect(() => {
        let projectIds = '';
        if (projectIdsQueryParam) projectIds = projectIdsQueryParam.split('^').filter((id) => id)?.join(',');
        getFilterList({ project_ids: projectIds, filter_labels: 'projects,district,locality,state,city,pin,org,enduser,requester' });
        getFilterList({ project_ids: projectIds, filter_labels: 'WAREHOUSE_OWNER,CLUSTER,SP,SCM,FE,PM,PSP,SUPERVISOR,REQUESTER,REMOTE_SUPPORT' });
    }, [getFilterList, projectIdsQueryParam, dispatch]);


    useEffect(() => {
        let { queryObject, filterStatus } = readFilterQueryParams(ticketFilterSchema, query);
        setFilterObject(queryObject);
        setFiltersActiveStatus(filterStatus);
        let isToShowFilterButton = false;
        Object.values(filterStatus).forEach((value) => (value ? (isToShowFilterButton = value) : false));
        setClearFilterButton(isToShowFilterButton);
        let transformedFilter = transformQueryFilters(ticketFilterSchema, queryObject);
        const isProjectSelected = transformedFilter && (!transformedFilter?.[ticketFilterSchema.projects.submitKey] || transformedFilter?.[ticketFilterSchema.projects.submitKey]?.length === 0)
        if (isProjectSelected) {
            delete transformedFilter.eventIds;
            delete transformedFilter.issueCategoryIds;
        }
        getTicketsList({
            searchTerm: searchQueryParam,
            limit: limitQueryParam,
            page: pageQueryParam,
            payload: transformedFilter,
            isFilterApplied: isToShowFilterButton,
            openedTab: openedMaintab,
        });
        return () => dispatch({ type: tickets.CLEAR_TICKET_LIST });
    }, [queryParams, searchQueryParam, limitQueryParam, pageQueryParam, setClearFilterButton, query, dispatch, openedSubTab, openedMaintab, getTicketsList]);

    useEffect(() => {
        let module = 'TICKET';
        if (!preferenceData[module] && [todayStatus.path, allTab.path].includes(openedMaintab)) {
            let role_id = newLoggedUserData.user.roles.map((role) => role.id).join(',');
            dispatch(getPreferences({
                type: 'COLUMN', module,
                org_id: newLoggedUserData.org.id,
                user_id: newLoggedUserData.user.id,
                role_id,
                level_id: getLevelId(openedMaintab)
            }));
        }
    }, [preferenceData, dispatch, openedMaintab, newLoggedUserData.user.id, newLoggedUserData.user.roles, newLoggedUserData.org.id]);

    const updateQuery = ({ filterList, destinationTab = openedMaintab, limit = limitQueryParam, page = pageQueryParam, filterSchema, query = searchQueryParam }) => {
        const currentParams = new URLSearchParams(window.location.search);
        const ticket_serial_number = currentParams.get('ticket_serial_number');
        const ticket_number = currentParams.get('ticket_number');
        const ticket_location_external_id = currentParams.get('ticket_location_external_id');
        const ticket_pincode = currentParams.get('ticket_pincode');
        const ticket_external_ids = currentParams.get('ticket_external_ids');

        updateQueryParamsForTickets({
            navigate,
            destinationTab,
            mainTab: openedMaintab,
            limit,
            page,
            filterList,
            filterSchema,
            query,
            defaultRowsPerPage,
            defaultPage,
            searchParams,
            setSearchParams,
            additionalParams: {
                ticket_serial_number,
                ticket_number,
                ticket_location_external_id,
                ticket_pincode,
                ticket_external_ids,
            }
        });
    };

    const searchFilterList = useMemo(() =>
        debounce((key, filter_label) => {
            if (key && filter_label) {
                getFilterList({ q: key, filter_labels: filter_label, formName: `filter_${filter_label}` })
            }
        }),
        [getFilterList], []);


    const filterComponent = ({ headName, keysToFilter, disable, disableText }) => {
        return <div className='flex text-font12'>
            {headName}
            <APIFilterPopup
                filterList={filterList}
                setFilterList={setFilterList}
                height={15}
                keysToFilter={keysToFilter}
                submit={({ payload, page, filterList }) => updateQuery({ filterSchema: ticketFilterSchema, payload, page, filterList })}
                filtersData={serviceRequestFilterList}
                filterSchema={ticketFilterSchema}
                filterQueryObject={filterQueryObject}
                isLoading={isTicketFilterListLoading}
                filtersActiveStatus={filtersActiveStatus}
                disable={disable}
                disableText={disableText}
                onSearch={searchFilterList}
            />
        </div>
    };

    const handleTicketNumberCLick = (ticket) => {
        let ticketSideDrawerPayload = {
            label: ticket.ticket_number,
            ticket_id: ticket.id,
        };
        dispatch(setTicketSideDrawer(true, 'ticketDetails', ticketSideDrawerPayload));
    };

    const editTicket = (ticket) => {
        navigate(`/tickets/${ticket.id}/edit`);
    };

    if (openedMaintab === todayStatus.path) {
        return <TodayStatusTable
            filterList={filterList}
            setFilterList={setFilterList}
            updateQuery={updateQuery}
            isTicketFilterListLoading={isTicketFilterListLoading}
            filtersActiveStatus={filtersActiveStatus}
            filterQueryObject={filterQueryObject}
            selectedColumns={selectedColumns}
            rowsPerPage={rowsPerPage}
            page={page}
            openedMaintab={openedMaintab}
        />
    }


    const possibleColumns = {
        ticket_number: {
            head: {
                render: (headName, checkedTicketTrackerLocal) => {
                    return (
                        <div className='flex items-center pl-2'>
                            {openedMaintab !== todayStatus.path ? <Checkbox
                                checkColor='text-scogoorange mr-2'
                                checked={serviceRequestList.data?.length > 0 && checkedTicketTrackerLocal?.size && serviceRequestList.data?.length === checkedTicketTrackerLocal?.size}
                                onChange={(event) => {
                                    selectALLHandler(event);
                                }}
                                dynamicSize={'1.2rem'}
                            /> : <></>}
                            {filterComponent({
                                headName,
                                keysToFilter: [ticketFilterSchema.executionDate,
                                ticketFilterSchema.ticketType],

                            })}
                        </div>
                    );
                },
                headName: 'Ticket No.',
                width: window.innerWidth * 0.12,
                isFixed: true,
                key: columnsKeys.id,
                columnRowCss: 'break-all',
            },
            body: {
                render: (ticket, rowIndex, checkedTicketTrackerLocal, handleOnChangeCheckboxLocal) => {
                    return <TicketNumber
                        ticket={ticket}
                        rowIndex={rowIndex}
                        checkedTicketTrackerLocal={checkedTicketTrackerLocal}
                        handleOnChangeCheckboxLocal={handleOnChangeCheckboxLocal}
                        ticketRemoteTerminal={ticketRemoteTerminal}
                        handleTicketNumberCLick={handleTicketNumberCLick}
                        showCheckBox={openedMaintab !== todayStatus.path}
                    />
                }
            }
        },
        org_details: {
            head: {
                render: (headName) => {
                    const filters = [ticketFilterSchema.customer, ticketFilterSchema.endUser].filter((filter) => {
                        if (filter.filterName === ticketFilterSchema.customer.filterName) {
                            return manageOtherOrgs;
                        }
                        return true;
                    });
                    return filterComponent({
                        headName,
                        keysToFilter: filters,
                    });
                },
                headName: 'Customer Details',
                isFixed: true,
                width: window.innerWidth * 0.11,
                key: columnsKeys.customer_details,
            },
            body: {
                render: (ticket) => {
                    return (
                        <div className='w-full'>
                            {manageOtherOrgs && (
                                <span className={`max-w-[95%] break-all`}>
                                    <p className={textLabelClass}>Customer </p>
                                    <span className={textValueClass}>
                                        <OverflowTip textClass={`text-inherit ${textValueClass}`} someLongText={ticket.organization.name} />
                                    </span>
                                </span>
                            )}


                            {ticket.end_user?.name && (
                                <span className={`max-w-[95%] break-all`}>
                                    <p className={textLabelClass}>End User </p>
                                    <span className={textValueClass}>
                                        <OverflowTip textClass={`text-inherit ${textValueClass}`} someLongText={ticket.end_user?.name} />
                                    </span>
                                </span>
                            )}
                            <span className={`max-w-[95%] break-all`}>
                                <p className={textLabelClass}>Project </p>
                                <span className='px-2 bg-scogoprimary text-white flex gap-2 items-center rounded-2xl w-fit max-w-11/12'>
                                    <OverflowTip textClass={`text-inherit ${textValueClass} py-[0.5]`} someLongText={validateStringValue(ticket?.project?.name)} />
                                    {canEditTicket(ticket) && (
                                        <>
                                            <span className='text-white'>|</span>
                                            <span
                                                className='material-icons text-font12 text-white pr-1 cursor-pointer '
                                                onClick={() => {
                                                    editTicket(ticket);
                                                }}
                                            >
                                                edit
                                            </span>
                                        </>
                                    )}
                                </span>
                            </span>
                        </div>
                    );
                },
            }
        },
        location_details: {
            head: {
                render: (headName, checkedTicketTrackerLocal) => {
                    return (
                        <div className='pl-2 flex items-center '>
                            {/* {showSiteAtFirst ? <Checkbox
                                checkColor='text-scogoorange mr-2'
                                checked={serviceRequestList.data?.length > 0 && checkedTicketTrackerLocal?.size && serviceRequestList.data?.length === checkedTicketTrackerLocal?.size}
                                onChange={(event) => {
                                    selectALLHandler(event);
                                }}
                                dynamicSize={'1.2rem'}
                            /> : <></>} */}
                            {filterComponent({
                                headName,
                                keysToFilter: [
                                    ticketFilterSchema.state,
                                    ticketFilterSchema.city,
                                    ticketFilterSchema.districts,
                                    ticketFilterSchema.localities,
                                    ticketFilterSchema.region_tier,
                                ],
                            })}
                        </div>
                    );
                },
                headName: 'Site Details',
                width: window.innerWidth * 0.14,
                isFixed: true,
                columnRowCss: 'break-all',
                key: columnsKeys.site_details,
            },
            body: {
                render: (ticket, rowIndex, checkedTicketTrackerLocal, handleOnChangeCheckboxLocal) => <SiteColumn
                    ticket={ticket}
                    rowIndex={rowIndex}
                    checkedTicketTrackerLocal={checkedTicketTrackerLocal}
                    handleOnChangeCheckboxLocal={handleOnChangeCheckboxLocal}
                    handleTicketNumberCLick={handleTicketNumberCLick}
                />,
            },
        },
        status: {
            head: {
                render: (headName) => {
                    let keysToFilter = [ticketFilterSchema.milestoneStatus];
                    return filterComponent({
                        headName,
                        keysToFilter
                    });
                },
                headName: 'Milestones',
                width: window.innerWidth * 0.07,
                hide: !selectedColumns.includes(ticketExtraColumns.current_stataus),
                key: columnsKeys.current_status,
                isFixed: true,
                columnRowCss: 'break-words',
            },
            body: {
                render: (ticket) => {
                    return <StatusColumn ticket_id={ticket.id} status={ticket.status} category={ticket.category} milestones={ticket.milestones} ticket={ticket} />;
                },
            },
        },
        desc: {
            head: {
                render: (headName) => {
                    return <div className='text-font12 pl-2'>{headName}</div>;
                },
                headName: 'Description',
                hide: !selectedColumns.includes(ticketExtraColumns.description),
                width: window.innerWidth * 0.1,
                key: columnsKeys.ticket_description,
                columnRowCss: 'break-all',
            },
            body: {
                render: (ticket) => {
                    return <RenderDescription ticket={ticket} />
                },
            },
        },
        price: {
            head: {
                render: (headName) => {
                    if (!manageOtherOrgs) return <div className='text-font12 pl-2'>{headName}</div>;
                    return filterComponent({
                        headName,
                        keysToFilter: [ticketFilterSchema.ticketInvoiceGenerated, ticketFilterSchema.financialState],
                    });
                },
                headName: 'Price',
                width: window.innerWidth * 0.0999,
                hide: !selectedColumns.includes(ticketExtraColumns.price),
                key: columnsKeys.price,
            },
            body: {
                render: (ticket) => <Price ticket={ticket} />,
            },
        },
        contact_details: {
            head: {
                render: (headName) => {
                    let keysToFilter = [ticketFilterSchema.clusters, ticketFilterSchema.psp, ticketFilterSchema.supervisor, ticketFilterSchema.agent];
                    // keysToFilter = keysToFilter.filter((key) => {
                    //     return false
                    // })

                    return filterComponent({
                        headName,
                        keysToFilter,
                    });
                },
                headName: 'Contact Details',
                width: window.innerWidth * 0.12,
                hide: !selectedColumns.includes(ticketExtraColumns.contact_details),
                columnRowCss: 'break-all',
                key: columnsKeys.contact_details,
            },
            body: {
                render: (ticket) => {
                    return <Contact ticket={ticket} allowRemoveClusterAndPsp />;
                },
            },
        },
        partner_details: {
            head: {
                render: (headName) => {
                    return filterComponent({
                        headName,
                        keysToFilter: [ticketFilterSchema.partners, ticketFilterSchema.engineers],
                    });
                },
                headName: 'Partner Details',
                hide: !selectedColumns.includes(ticketExtraColumns.partner_details),
                width: window.innerWidth * 0.14,
                key: columnsKeys.partner_details,
            },
            body: {
                render: (ticket) => <PartnerDetail ticket={ticket} loading={ticketAssetRemarkLoading} />,
            },
        },
        progress: {
            head: {
                render: (headName) => {
                    return filterComponent({
                        headName,
                        keysToFilter: [ticketFilterSchema.orderDate, ticketFilterSchema.siteComplete, ticketFilterSchema.signOffApprovalDate, ticketFilterSchema.requestedBy],
                    });
                },
                headName: 'Progress',
                width: window.innerWidth * 0.2,
                hide: !selectedColumns.includes(ticketExtraColumns.progress),
                key: columnsKeys.progress,
            },
            body: {
                render: (ticket) => <ProgressBar ticket={ticket} />,
            },
        },
        assets: {
            head: {
                render: (headName) => {
                    return <div className='text-font12'>{headName}</div>;
                },
                headName: 'Assets',
                width: window.innerWidth * 0.15,
                hide: !selectedColumns.includes(ticketExtraColumns.assets),
                key: columnsKeys.assets,
            },
            body: {
                render: (ticket) => {
                    return <AssetsColumn ticket={ticket} loading={ticketAssetRemarkLoading} onINeedRMASupportClick={onINeedRMASupportClick} />;
                },
            },
        },
        remark: {
            head: {
                render: (headName) => {
                    return filterComponent({
                        headName,
                        keysToFilter: [ticketFilterSchema.remarksFilter],
                    });
                },
                headName: 'Remarks',
                width: window.innerWidth * 0.15,
                hide: !selectedColumns.includes(ticketExtraColumns.remark),
                key: columnsKeys.remark,
            },
            body: {
                render: (ticket, rowIndex) => (
                    <RemarkColumn ticket_id={ticket.id} loading={ticketAssetRemarkLoading} />
                ),
            },
        },
        shipping_details: {
            head: {
                render: (headName) => {
                    return <div className='text-font12'>{headName}</div>;
                },
                headName: 'Shipping Details',
                width: window.innerWidth * 0.15,
                columnRowCss: 'break-all',
                hide: !selectedColumns.includes(ticketExtraColumns.shipping_details),
                key: columnsKeys.shipping_details,
            },
            body: {
                render: (ticket) => {
                    return <Shipping ticket={ticket} />;
                },
            },
        },
        actions: {
            head: {
                render: (headName) => {
                    return <div className='text-font12 m-auto'>{headName}</div>;
                },
                headName: 'Actions',
                width: window.innerWidth * 0.1,
                key: columnsKeys.action,
            },
            body: {
                render: (ticket) => {
                    return (
                        <div className='flex justify-center flex-end m-auto'>
                            <TicketAction ticket={ticket} />
                        </div>
                    );
                },
            },
        }
    };

    let filteredTableColumnsSchema = { table: [] };
    if (ticketColumns && ticketColumns[getLevelId(openedMaintab)]) {
        let columns = ticketColumns[getLevelId(openedMaintab)]?.filter(elem => elem.is_visible)
            .map(({ key }) => possibleColumns[key])
            .filter(Boolean);

        filteredTableColumnsSchema = {
            table: columns
        };
    };

    if (isMobileView) {
        return <div className='w-full h-full overflow-y-auto mt-8'>
            {serviceRequestList?.data?.map((item, index) => {
                // let { milestone, statusToPrint } = getStatusToPrint(item)
                return <div
                    key={item?.id}
                    className={`p-4 rounded-lg shadow-md ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} 
                    border border-gray-200 m-2 hover:shadow-lg transition-shadow duration-200`}
                // onClick={() => handleTicketNumberCLick(item)}
                >
                    <div className="flex justify-between items-start mb-2">
                        {item?.ticket_number &&
                            <div className='text-xl font-bold text-gray-800'>
                                {item?.ticket_number}
                            </div>
                        }
                        <DateComponent
                            className="text-scogoorange font-medium"
                            date={item.created_at}
                            text={false}
                            timeFormat='DD MMM YYYY'
                        />
                        <TicketAction ticket={item} />
                    </div>

                    <div className="space-y-2 mb-3">
                        {item?.requester?.display_name &&
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Requested By:</span>
                                <span className="text-gray-700 font-medium">{item?.requester?.display_name}</span>
                            </div>
                        }

                        {item?.location?.external_id &&
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Site ID:</span>
                                <span className="text-gray-700 font-medium">{item?.location?.external_id}</span>
                            </div>
                        }

                        {item?.project?.name &&
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Project:</span>
                                <span className="text-gray-700 font-medium">{item?.project?.name}</span>
                            </div>
                        }

                        {item?.location?.address &&
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Address:</span>
                                <span className="text-gray-700 font-medium truncate">{item?.location?.address}</span>
                            </div>
                        }
                    </div>

                    <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200">
                        {/* <StatusLabel
                            onClick={(e) => {
                                e.stopPropagation();
                                let approveReject = false;
                                if (milestone) {
                                    approveReject = isIndividualMilestoneStatusInRange(milestone, MilestoneSubStatus.SIGN_OFF_REQUESTED, MilestoneSubStatus.SIGN_OFF_ACCEPTED);
                                }
                                if (approveReject) {
                                    navigate(`${ticket.path}/${item?.id}/edit-payout?source=ticket?milestone_id=${milestone.id}`)
                                }
                            }}
                            labelClass={`${MilestoneSubStatusColor[statusToPrint]} font-medium py-1 px-3 text-sm`}
                            titleText='Milestone Status'
                            hoverText={statusToPrint}
                            content={`${milestone?.type ? milestone?.type : statusToPrint}`}
                        /> */}
                    </div>
                </div>
            })}
        </div>
    }

    return (
        <FrozenTable
            rowsData={serviceRequestList.data}
            frozenColumnNum={showTicketSideDrawer ? 2 : 4}
            schema={filteredTableColumnsSchema}
            rowsPerPage={rowsPerPage}
            page={page}
            tablePagination
            count={memoizedServiceRequestCount}
            tablePaginationHandler={(limit, page) => updateQuery({ limit, page, filterSchema: ticketFilterSchema, filterList })}
            handleOnChangeCheckbox={handleOnChangeCheckbox}
            checkedTicketTracker={checkedTicketTracker}
            isLoading={isServiceRequestListLoading}
            tableError='No Tickets Found'
            rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]}
        />
    );
});

export default TicketsTable;



const RenderDescription = ({ ticket }) => {
    const isMissedCall = ticket && ticket.remarks && ticket.remarks.find((elem) => elem.action === TicketMilestoneEvents.INBOUND_MISSED)
    return <div className='w-full pl-2'>
        <OwnerColumn ticket={ticket} />
        {isMissedCall && <div className="text-red-500 text-xs font-medium py-1">Missed Call</div>}
        {ticket.title && <OverflowTip textClass={'text-font11 text-black'} someLongText={`${ticket.title}`} />}
        {ticket.description && (
            <LineClampComponent textCss={textValueClass + ' text-scogogray'} value={ticket.description} lineClamp='line-clamp-3' />
        )}
    </div>
}